import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Grid,
  MenuItem,
  Chip,
  Link,
  useTheme,
  FormHelperText,
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { toast } from "../../common/AppToast";
import { filledFieldWrapSx, filledUploadDropzoneSx } from "./listNewAssetFilledSurface";

const MAX_FILE_MB = 10;

const AUTHORIZED_SIGNATORY_IMAGE_TYPES = new Set(["image/jpeg", "image/jpg", "image/png"]);

function isAuthorizedSignatoryImageFile(file) {
  const type = (file.type || "").toLowerCase();
  if (AUTHORIZED_SIGNATORY_IMAGE_TYPES.has(type)) return true;
  if (type) return false;
  return /\.(jpe?g|png)$/i.test(file.name || "");
}

const JURISDICTIONS = [
  "Saudi Arabia (KSA)",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
  "Other",
];

const labelSx = { fontWeight: 600, fontSize: 14, color: "text.primary", mb: 0.75 };

function RequiredLabel({ children }) {
  return (
    <Typography component="span" sx={labelSx}>
      {children}{" "}
      <Box component="span" sx={{ color: "error.main" }}>
        *
      </Box>
    </Typography>
  );
}

function FieldLabel({ children }) {
  return (
    <Typography component="span" sx={labelSx}>
      {children}
    </Typography>
  );
}

export default function ListNewAssetStep4({
  draft,
  creationData,
  onDraftChange,
  nextStep,
  prevStep,
  wizardStep = 4,
  wizardTotalSteps = 8,
  categoryChipLabel = "Real Estate",
  isEditMode = false,
}) {
  const theme = useTheme();
  const TEAL = theme.palette.brand.cyan;
  const TEAL_HOVER = theme.palette.brand.cyanDark;

  // Cyan focus/hover override — matches the Property Specifications pattern from Step2.
  const cyanInputSx = {
    "& .MuiOutlinedInput-root": {
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.brand.cyan,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.brand.cyanDark,
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.brand.cyanDark,
    },
    "& .MuiSelect-icon": {
      color: theme.palette.brand.cyan,
    },
  };

  // Borderless flat-input style — matches Asset Name / Description in Step2.
  const flatInputSx = {
    "& .MuiInputBase-input": {
      px: 2,
      py: 1.25,
      fontSize: 14,
    },
  };

  const spvName = draft.spvName ?? "";
  const registrationNumber = draft.registrationNumber ?? "";
  const jurisdiction = draft.jurisdiction ?? "";
  const structureType = draft.structureType ?? "";
  const formationDate = draft.formationDate ?? "";
  const registeredAddress = draft.registeredAddress ?? "";
  const trn = draft.trn ?? "";
  const authorizedSignatory = draft.authorizedSignatoryFile;
  const licenseExpiryDate = draft.licenseExpiryDate ?? "";
  const purpose = draft.purpose ?? "";
  const spvCertificateFiles = draft.spvCertificateFiles || [];

  const structureTypeOptions = useMemo(
    () => creationData?.spvStructureTypeOptions ?? {},
    [creationData?.spvStructureTypeOptions]
  );
  const structureEntries = useMemo(
    () => Object.entries(structureTypeOptions),
    [structureTypeOptions]
  );

  const [errors, setErrors] = useState({});
  const authInputRef = useRef(null);
  const certInputRef = useRef(null);

  const validateFileSize = (file) => {
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`File must be ${MAX_FILE_MB}MB or smaller.`);
      return false;
    }
    return true;
  };

  const handleAuthFile = (e) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    if (!isAuthorizedSignatoryImageFile(file)) {
      setErrors((p) => ({ ...p, authorizedSignatoryFile: "Only JPG or PNG images are allowed (not PDF)." }));
      input.value = "";
      return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setErrors((p) => ({ ...p, authorizedSignatoryFile: `File must be ${MAX_FILE_MB}MB or smaller.` }));
      input.value = "";
      return;
    }
    onDraftChange({ authorizedSignatoryFile: file });
    setErrors((p) => ({ ...p, authorizedSignatoryFile: "" }));
  };

  const handleCertFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const ok = files.every(validateFileSize);
    if (!ok) return;
    onDraftChange({ spvCertificateFiles: files });
    setErrors((p) => ({ ...p, spvCertificateFiles: "" }));
  };

  const handleNext = () => {
    const newErrors = {};
    if (!spvName.trim()) newErrors.spvName = "SPV name is required.";
    if (!registrationNumber.trim()) newErrors.registrationNumber = "Registration number is required.";
    if (!jurisdiction) newErrors.jurisdiction = "Jurisdiction is required.";
    const hasSignatory =
      draft.authorizedSignatoryFile ||
      (isEditMode && Boolean(draft._editMeta?.spvSignatureUrl));
    if (!hasSignatory) newErrors.authorizedSignatoryFile = "Please upload the authorized signatory.";
    if (!purpose.trim()) newErrors.purpose = "Purpose is required.";
    const hasSpvDocs =
      spvCertificateFiles.length > 0 ||
      (isEditMode && Array.isArray(draft._editMeta?.spvDocumentUrls) && draft._editMeta.spvDocumentUrls.length > 0);
    if (!hasSpvDocs) newErrors.spvCertificateFiles = "Please upload SPV certificate / documents.";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    nextStep();
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: 1200,
        mx: "auto",
        minHeight: "100vh",
      }}
    >

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 2 }}
      >
        <Link
          component="button"
          type="button"
          underline="none"
          onClick={prevStep}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            color: TEAL,
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            border: "none",
            background: "none",
            p: 0,
            "&:hover": { color: TEAL_HOVER },
          }}
        >
          <ArrowBackIosNewRoundedIcon sx={{ fontSize: 16 }} />
          Back
        </Link>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            label={categoryChipLabel}
            size="small"
            sx={{
              bgcolor: (theme) => theme.palette.brand.cyanSoft,
              color: (theme) => theme.palette.brand.cyanDark,
              fontWeight: 600,
              fontSize: 12,
              height: 26,
            }}
          />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.muted" }}>
            Step {wizardStep} of {wizardTotalSteps}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={0.75} sx={{ mb: 3 }}>
        {Array.from({ length: wizardTotalSteps }).map((_, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: 6,
              borderRadius: 999,
              bgcolor: i < wizardStep ? TEAL : theme.palette.border.light,
            }}
          />
        ))}
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3, md: 4 },
          borderRadius: "14px",
          bgcolor: "background.paper",
          boxShadow: (theme) => theme.palette.shadow.cardStrong,
        }}
      >
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "text.primary", mb: 0.75 }}
            >
              SPV Details
            </Typography>
            <Typography variant="body2" sx={{ color: "text.muted", mb: 3 }}>
              Enter the Special Purpose Vehicle details for this asset.
            </Typography>

            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={filledFieldWrapSx(theme, !!spvName.trim())}>
                  <RequiredLabel>SPV Name</RequiredLabel>
                  <TextField
                    variant="standard"
                    placeholder="e.g. Marina Heights SPV LLC"
                    fullWidth
                    value={spvName}
                    onChange={(e) => { onDraftChange({ spvName: e.target.value }); setErrors((p) => ({ ...p, spvName: "" })); }}
                    InputProps={{ disableUnderline: true }}
                    sx={{ mt: 1, ...flatInputSx }}
                  />
                  {errors.spvName && <FormHelperText error sx={{ mt: 0.5 }}>{errors.spvName}</FormHelperText>}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={filledFieldWrapSx(theme, !!registrationNumber.trim())}>
                  <RequiredLabel>Registration Number</RequiredLabel>
                  <TextField
                    variant="standard"
                    placeholder="e.g. REG-2024-001"
                    fullWidth
                    value={registrationNumber}
                    onChange={(e) => { onDraftChange({ registrationNumber: e.target.value }); setErrors((p) => ({ ...p, registrationNumber: "" })); }}
                    InputProps={{ disableUnderline: true }}
                    sx={{ mt: 1, ...flatInputSx }}
                  />
                  {errors.registrationNumber && <FormHelperText error sx={{ mt: 0.5 }}>{errors.registrationNumber}</FormHelperText>}
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={filledFieldWrapSx(theme, !!jurisdiction)}>
                  <RequiredLabel>Jurisdiction</RequiredLabel>
                <TextField
                  variant="standard"
                  select
                  fullWidth
                  value={jurisdiction}
                  onChange={(e) => { onDraftChange({ jurisdiction: e.target.value }); setErrors((p) => ({ ...p, jurisdiction: "" })); }}
                  InputProps={{ disableUnderline: true }}
                  sx={{ mt: 1, ...flatInputSx }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (v) =>
                      v ? (
                        v
                      ) : (
                        <Typography sx={{ color: "text.faint" }}>Select jurisdiction</Typography>
                      ),
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Select jurisdiction</em>
                  </MenuItem>
                  {JURISDICTIONS.map((j) => (
                    <MenuItem key={j} value={j}>
                      {j}
                    </MenuItem>
                  ))}
                </TextField>
                {errors.jurisdiction && <FormHelperText error sx={{ mt: 0.5 }}>{errors.jurisdiction}</FormHelperText>}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={filledFieldWrapSx(theme, !!structureType)}>
                  <FieldLabel>Structure Type</FieldLabel>
                <TextField
                  variant="standard"
                  select
                  fullWidth
                  value={structureType}
                  onChange={(e) => { onDraftChange({ structureType: e.target.value }); setErrors((p) => ({ ...p, structureType: "" })); }}
                  InputProps={{ disableUnderline: true }}
                  sx={{ mt: 1, ...flatInputSx }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (v) =>
                      v ? (
                        structureTypeOptions[v] || v
                      ) : (
                        <Typography sx={{ color: "text.faint" }}>Select structure</Typography>
                      ),
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Select structure</em>
                  </MenuItem>
                  {structureEntries.map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
                {errors.structureType && <FormHelperText error sx={{ mt: 0.5 }}>{errors.structureType}</FormHelperText>}
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={filledFieldWrapSx(theme, !!formationDate)}>
                  <FieldLabel>Formation Date</FieldLabel>
                  <Box sx={{ display: "flex" }}>
                    <TextField
                      type="date"
                      value={formationDate}
                      onChange={(e) => { onDraftChange({ formationDate: e.target.value }); setErrors((p) => ({ ...p, formationDate: "" })); }}
                      sx={{
                        mt: 2,
                        width: 220,
                        ...cyanInputSx,
                        "& .MuiOutlinedInput-root": {
                          ...cyanInputSx["& .MuiOutlinedInput-root"],
                          borderRadius: "6px",
                        },
                        "& .MuiOutlinedInput-input": { py: 1.25 },
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                  {errors.formationDate && <FormHelperText error sx={{ mt: 0.5 }}>{errors.formationDate}</FormHelperText>}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={filledFieldWrapSx(theme, !!registeredAddress.trim())}>
                  <FieldLabel>Registered Address</FieldLabel>
                  <TextField
                    variant="standard"
                    placeholder="e.g. 123 Corporate Blvd, Suite 100"
                    fullWidth
                    value={registeredAddress}
                    onChange={(e) => { onDraftChange({ registeredAddress: e.target.value }); setErrors((p) => ({ ...p, registeredAddress: "" })); }}
                    InputProps={{ disableUnderline: true }}
                    sx={{ mt: 1, ...flatInputSx }}
                  />
                  {errors.registeredAddress && <FormHelperText error sx={{ mt: 0.5 }}>{errors.registeredAddress}</FormHelperText>}
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={filledFieldWrapSx(theme, !!trn.trim())}>
                  <FieldLabel>Tax Registration Number (TRN)</FieldLabel>
                  <TextField
                    variant="standard"
                    placeholder="e.g. TRN-99887766"
                    fullWidth
                    value={trn}
                    onChange={(e) => { onDraftChange({ trn: e.target.value }); setErrors((p) => ({ ...p, trn: "" })); }}
                    InputProps={{ disableUnderline: true }}
                    sx={{ mt: 1, ...flatInputSx }}
                  />
                  {errors.trn && <FormHelperText error sx={{ mt: 0.5 }}>{errors.trn}</FormHelperText>}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <RequiredLabel>Authorized Signatory</RequiredLabel>
                <input
                  ref={authInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png"
                  hidden
                  onChange={handleAuthFile}
                />
                <Paper
                  component="button"
                  type="button"
                  onClick={() => authInputRef.current?.click()}
                  elevation={0}
                  sx={{
                    mt: 1,
                    width: "100%",
                    minHeight: 96,
                    borderRadius: 2,
                    bgcolor: "background.subtle",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    p: 2,
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: (theme) => theme.palette.brand.cyan,
                      bgcolor: "background.paper",
                    },
                    ...filledUploadDropzoneSx(
                      theme,
                      Boolean(authorizedSignatory),
                      theme.palette.border.medium
                    ),
                  }}
                >
                  <FileUploadOutlinedIcon
                    sx={{ color: "text.faint", fontSize: 22 }}
                  />
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "text.muted",
                    }}
                  >
                    Upload Authorization
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "text.faint", textAlign: "center" }}>
                    JPG or PNG only
                  </Typography>
                </Paper>
                {authorizedSignatory && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: (theme) => theme.palette.brand.cyanDark,
                      fontWeight: 600,
                      mt: 0.5,
                    }}
                  >
                    {authorizedSignatory.name}
                  </Typography>
                )}
                {errors.authorizedSignatoryFile && (
                  <FormHelperText error sx={{ mt: 0.5 }}>{errors.authorizedSignatoryFile}</FormHelperText>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={filledFieldWrapSx(theme, !!licenseExpiryDate)}>
                  <FieldLabel>License Expiry Date</FieldLabel>
                  <Box sx={{ display: "flex" }}>
                    <TextField
                      type="date"
                      value={licenseExpiryDate}
                      onChange={(e) => { onDraftChange({ licenseExpiryDate: e.target.value }); setErrors((p) => ({ ...p, licenseExpiryDate: "" })); }}
                      sx={{
                        mt: 1,
                        width: 220,
                        ...cyanInputSx,
                        "& .MuiOutlinedInput-root": {
                          ...cyanInputSx["& .MuiOutlinedInput-root"],
                          borderRadius: "6px",
                        },
                        "& .MuiOutlinedInput-input": { py: 1.25 },
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                  {errors.licenseExpiryDate && <FormHelperText error sx={{ mt: 0.5 }}>{errors.licenseExpiryDate}</FormHelperText>}
                </Box>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Box sx={filledFieldWrapSx(theme, !!purpose.trim())}>
                  <RequiredLabel>Purpose</RequiredLabel>
                  <TextField
                    variant="standard"
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Describe the operational purpose of this SPV..."
                    value={purpose}
                    onChange={(e) => { onDraftChange({ purpose: e.target.value }); setErrors((p) => ({ ...p, purpose: "" })); }}
                    InputProps={{ disableUnderline: true }}
                    sx={{ mt: 1, ...flatInputSx }}
                  />
                  {errors.purpose && <FormHelperText error sx={{ mt: 0.5 }}>{errors.purpose}</FormHelperText>}
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                  <DescriptionOutlinedIcon
                    sx={{
                      color: (theme) => theme.palette.brand.cyan,
                      fontSize: 22,
                    }}
                  />
                  <RequiredLabel>SPV Certificate / Documents</RequiredLabel>
                </Stack>
                <Typography sx={{ fontSize: 13, color: "text.muted", mb: 1 }}>
                  Upload Certificate of Incorporation or similar documents.
                </Typography>
                <input
                  ref={certInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  multiple
                  hidden
                  onChange={handleCertFiles}
                />
                <Paper
                  component="button"
                  type="button"
                  onClick={() => certInputRef.current?.click()}
                  elevation={0}
                  sx={{
                    width: "100%",
                    minHeight: 160,
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    p: 2,
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: (theme) => theme.palette.brand.cyan,
                      bgcolor: "background.subtle",
                    },
                    ...filledUploadDropzoneSx(
                      theme,
                      spvCertificateFiles.length > 0,
                      theme.palette.accent.teal.dashedBorder
                    ),
                  }}
                >
                  <Stack alignItems="center" spacing={1}>
                    <CloudUploadOutlinedIcon
                      sx={{
                        color: (theme) => theme.palette.brand.cyan,
                        fontSize: 40,
                      }}
                    />
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}
                    >
                      Click to upload SPV documents. PDF, JPG, PNG up to 10MB
                    </Typography>
                    {!!spvCertificateFiles.length && (
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: (theme) => theme.palette.brand.cyanDark,
                          fontWeight: 600,
                        }}
                      >
                        {spvCertificateFiles.length} file
                        {spvCertificateFiles.length > 1 ? "s" : ""} selected
                      </Typography>
                    )}
                  </Stack>
                </Paper>
                {errors.spvCertificateFiles && (
                  <FormHelperText error sx={{ mt: 0.5 }}>{errors.spvCertificateFiles}</FormHelperText>
                )}
              </Grid>
            </Grid>
          </Grid>

          {/* <Grid size={{ xs: 12, lg: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                bgcolor: (theme) => theme.palette.brand.cyanSoft,
                border: (theme) =>
                  `1px solid ${theme.palette.accent.teal.border}`,
                position: { lg: "sticky" },
                top: { lg: 16 },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 2 }}>
                <AutoAwesomeOutlinedIcon
                  sx={{
                    color: (theme) => theme.palette.accent.teal.text,
                    fontSize: 22,
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 16,
                    color: (theme) => theme.palette.accent.teal.text,
                  }}
                >
                  Smart Recommendations
                </Typography>
              </Stack>
              <Stack spacing={1.5}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: "background.paper",
                    border: (theme) =>
                      `1px solid ${theme.palette.accent.teal.border}`,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 13, color: "text.neutral", lineHeight: 1.5 }}
                  >
                    For real estate, we recommend <strong>Saudi Arabia (KSA)</strong> for optimal
                    regulatory compliance.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: "background.paper",
                    border: (theme) =>
                      `1px solid ${theme.palette.accent.teal.border}`,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 13, color: "text.neutral", lineHeight: 1.5 }}
                  >
                    A <strong>Limited Liability Company (LLC)</strong> structure is most common
                    for this asset class.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: "background.paper",
                    border: (theme) =>
                      `1px solid ${theme.palette.accent.teal.border}`,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 13, color: "text.neutral", lineHeight: 1.5 }}
                  >
                    Ensure your SPV is registered for VAT if expected turnover exceeds the
                    threshold.
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid> */}
        </Grid>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 4, pt: 2 }}
        >
          <Button
            variant="outlined"
            onClick={prevStep}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderColor: (theme) => theme.palette.border.medium,
              color: "text.neutral",
              px: 3,
              py: 1,
              "&:hover": {
                borderColor: (theme) => theme.palette.text.faint,
                bgcolor: "background.subtle",
              },
            }}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: 14 }} />}
            onClick={handleNext}
            sx={{
              px: 1.5,
              py: 1,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: TEAL,
              borderRadius: "12px",
              boxShadow: "none",
              "&:hover": { bgcolor: TEAL_HOVER, boxShadow: "none" },
            }}
          >
            Next Step
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
